import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';

// nextLesson is a calendar day. Parse/format it in the studio's local (Central)
// frame so the date input, the stored value, and the roster all agree. A UTC
// round-trip renders one day early — and legacy saves stored the day as UTC
// midnight, which we normalize back to its intended calendar day here (#14).
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/;
const toLocalDay = (val) => {
	if (!val) return null;
	const m = typeof val === 'string' && val.match(DATE_ONLY_RE);
	const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(val);
	return isNaN(d) ? null : d;
};
const toDateInputValue = (val) => {
	const d = toLocalDay(val);
	if (!d) return '';
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EditStudent = ({ show, onHide, student, onSuccess }) => {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [formData, setFormData] = useState({
		// Basic Info
		firstName: '',
		lastName: '',
		fullName: '',
		email: '',
		phone: '',
		birthday: '',
		age: '',

		// Lesson Info
		instrument: '',
		school: '',
		status: 'Active',
		nextLesson: '',
		defaultLessonLength: 45,

		// Billing
		// RETIRED FIELDS — do not re-add either one. Both are MMS-era leftovers,
		// deliberately absent from this form rather than forgotten. Each looked
		// authoritative sitting next to live billing facts while deciding nothing.
		//
		//   students.makeupCredits (int) — credits are a LEDGER now: the
		//   `makeupCredits` COLLECTION holds the deltas and students.makeupBalance
		//   is the counter, co-committed in one transaction by
		//   functions/makeupCredits.js. Point any new credit UI at makeupBalance.
		//   Retired 2026-08-09; decision Anthony 2026-07-14 (MMS credits do not
		//   carry into fall — the ledger starts at zero for everyone).
		//
		//   students.price (string) — NO billing code reads it. Tuition resolves
		//   from src/canon/season.json at generation time and is stamped onto the
		//   invoice; functions/billing.js never consults the student doc for a
		//   rate. Measured in prod 2026-08-09 before removal: 60 of 64 students
		//   held the byte-identical string "$220.00" — last season's installment
		//   against this season's $240 — plus one "$0.00" and three blanks. It
		//   was a constant, and a stale one. Retired 2026-08-09 (Anthony).
		//   If a real per-student rate is ever needed (a comp, a bespoke
		//   arrangement), design it against the invoice model — do not revive a
		//   free-text field that nothing validates and nothing reads.
		//
		// Stored values are left untouched on purpose in BOTH cases: tombstone,
		// not deletion.
		billingType: '',

		// Parent 1
		parent1_firstName: '',
		parent1_lastName: '',
		parent1_email: '',
		parent1_phone: '',
		parent1_address: '',

		// Parent 2
		parent2_firstName: '',
		parent2_lastName: '',
		parent2_email: '',
		parent2_phone: '',

		// Additional
		referrer: '',
		dateStarted: '',
		notes: '',
	});

	useEffect(() => {
		if (student) {
			setFormData({
				firstName: student.firstName || '',
				lastName: student.lastName || '',
				fullName: student.fullName || '',
				email: student.email || '',
				phone: student.phone || '',
				birthday: student.birthday || '',
				age: student.age || '',
				instrument: student.instrument || '',
				school: student.school || '',
				status: student.status || 'Active',
				nextLesson: toDateInputValue(student.nextLesson),
				defaultLessonLength: student.defaultLessonLength || 45,
				billingType: student.billingType || '',
				parent1_firstName: student.parent1?.firstName || '',
				parent1_lastName: student.parent1?.lastName || '',
				parent1_email: student.parent1?.email || '',
				parent1_phone: student.parent1?.phone || '',
				parent1_address: student.parent1?.address || '',
				parent2_firstName: student.parent2?.firstName || '',
				parent2_lastName: student.parent2?.lastName || '',
				parent2_email: student.parent2?.email || '',
				parent2_phone: student.parent2?.phone || '',
				referrer: student.referrer || '',
				dateStarted: student.dateStarted || '',
				notes: student.notes || '',
			});
		}
	}, [student]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleSave = async () => {
		if (!student || !student.id) return;

		// Check if student is locked
		if (student.locked) {
			setError('This student is locked and cannot be edited. Please unlock first.');
			return;
		}

		setSaving(true);
		setError(null);

		try {
			const studentRef = doc(db, 'students', student.id);

			// Prepare update data
			const updateData = {
				firstName: formData.firstName,
				lastName: formData.lastName,
				fullName: formData.fullName || `${formData.firstName} ${formData.lastName}`,
				email: formData.email,
				phone: formData.phone,
				birthday: formData.birthday,
				age: formData.age ? parseInt(formData.age) : null,
				instrument: formData.instrument,
				school: formData.school,
				status: formData.status,
				// Parse the date-only input as LOCAL (Central) midnight so it round-trips
				// to the same calendar day on the roster (#14).
				nextLesson: formData.nextLesson ? new Date(formData.nextLesson + 'T00:00:00').toISOString() : null,
				defaultLessonLength: parseInt(formData.defaultLessonLength) || 45,
				billingType: formData.billingType,
				// Neither makeupCredits nor price is written here — see the retired
				// field note in the initial state above. These lines mattered most:
				// with the inputs gone but the writes kept, every ordinary student
				// save would have silently overwritten the stored MMS values (0 for
				// makeupCredits, '' for price), which is the opposite of a tombstone.
				parent1: {
					firstName: formData.parent1_firstName,
					lastName: formData.parent1_lastName,
					email: formData.parent1_email,
					phone: formData.parent1_phone,
					address: formData.parent1_address,
				},
				parent2: {
					firstName: formData.parent2_firstName,
					lastName: formData.parent2_lastName,
					email: formData.parent2_email,
					phone: formData.parent2_phone,
				},
				referrer: formData.referrer,
				dateStarted: formData.dateStarted,
				notes: formData.notes,
			};

			await updateDoc(studentRef, updateData);

			if (onSuccess) onSuccess();
			onHide();
		} catch (err) {
			console.error('Error updating student:', err);
			setError(err.message || 'Failed to update student. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	if (!student) return null;

	return (
		<Modal show={show} onHide={onHide} size='xl'>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className='fa-sharp fa-solid fa-edit me-2'></i>
					Edit Student: {student.fullName || `${student.firstName} ${student.lastName}`}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error && (
					<Alert variant='danger' onClose={() => setError(null)} dismissible>
						<i className='fa-sharp fa-solid fa-exclamation-triangle me-2'></i>
						{error}
					</Alert>
				)}

				{student.locked && (
					<Alert variant='warning'>
						<i className='fa-sharp fa-solid fa-lock me-2'></i>
						<strong>Student Locked:</strong> This student is protected and cannot be edited. Unlock the student first to make changes.
					</Alert>
				)}

				<Tabs defaultActiveKey='basic' className='mb-3'>
					{/* Basic Info Tab */}
					<Tab eventKey='basic' title='Basic Info'>
						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>First Name *</Form.Label>
									<Form.Control
										type='text'
										name='firstName'
										value={formData.firstName}
										onChange={handleChange}
										disabled={student.locked}
										required
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Last Name *</Form.Label>
									<Form.Control
										type='text'
										name='lastName'
										value={formData.lastName}
										onChange={handleChange}
										disabled={student.locked}
										required
									/>
								</Form.Group>
							</Col>
						</Row>

						<Form.Group className='mb-3'>
							<Form.Label>Full Name (Display Name)</Form.Label>
							<Form.Control
								type='text'
								name='fullName'
								value={formData.fullName}
								onChange={handleChange}
								disabled={student.locked}
								placeholder='Auto-filled if empty'
							/>
						</Form.Group>

						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Email</Form.Label>
									<Form.Control
										type='email'
										name='email'
										value={formData.email}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Phone</Form.Label>
									<Form.Control
										type='tel'
										name='phone'
										value={formData.phone}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Birthday</Form.Label>
									<Form.Control
										type='text'
										name='birthday'
										value={formData.birthday}
										onChange={handleChange}
										disabled={student.locked}
										placeholder='MM/DD/YYYY'
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Age</Form.Label>
									<Form.Control
										type='number'
										name='age'
										value={formData.age}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Form.Group className='mb-3'>
							<Form.Label>School</Form.Label>
							<Form.Control
								type='text'
								name='school'
								value={formData.school}
								onChange={handleChange}
								disabled={student.locked}
							/>
						</Form.Group>
					</Tab>

					{/* Lesson Info Tab */}
					<Tab eventKey='lessons' title='Lesson Info'>
						<Form.Group className='mb-3'>
							<Form.Label>Instrument</Form.Label>
							<Form.Control
								type='text'
								name='instrument'
								value={formData.instrument}
								onChange={handleChange}
								disabled={student.locked}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Status</Form.Label>
							<Form.Select
								name='status'
								value={formData.status}
								onChange={handleChange}
								disabled={student.locked}
							>
								<option value='Active'>Active</option>
								<option value='Inactive'>Inactive</option>
								<option value='On Hold'>On Hold</option>
								<option value='Waitlist'>Waitlist</option>
							</Form.Select>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Next Lesson Date</Form.Label>
							<Form.Control
								type='date'
								name='nextLesson'
								value={formData.nextLesson}
								onChange={handleChange}
								disabled={student.locked}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Default Lesson Length (minutes)</Form.Label>
							<Form.Control
								type='number'
								name='defaultLessonLength'
								value={formData.defaultLessonLength}
								onChange={handleChange}
								disabled={student.locked}
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Date Started</Form.Label>
							<Form.Control
								type='text'
								name='dateStarted'
								value={formData.dateStarted}
								onChange={handleChange}
								disabled={student.locked}
								placeholder='MM/DD/YYYY'
							/>
						</Form.Group>
					</Tab>

					{/* Billing Tab */}
					<Tab eventKey='billing' title='Billing'>
						<Form.Group className='mb-3'>
							<Form.Label>Billing Type</Form.Label>
							<Form.Select
								name='billingType'
								value={formData.billingType}
								onChange={handleChange}
								disabled={student.locked}
							>
								<option value=''>Select...</option>
								<option value='Monthly'>Monthly</option>
								<option value='Per Lesson'>Per Lesson</option>
								<option value='Package'>Package</option>
								<option value='Semester'>Semester</option>
							</Form.Select>
						</Form.Group>


					</Tab>

					{/* Parent 1 Tab */}
					<Tab eventKey='parent1' title='Parent/Guardian 1'>
						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>First Name</Form.Label>
									<Form.Control
										type='text'
										name='parent1_firstName'
										value={formData.parent1_firstName}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Last Name</Form.Label>
									<Form.Control
										type='text'
										name='parent1_lastName'
										value={formData.parent1_lastName}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Email</Form.Label>
									<Form.Control
										type='email'
										name='parent1_email'
										value={formData.parent1_email}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Phone</Form.Label>
									<Form.Control
										type='tel'
										name='parent1_phone'
										value={formData.parent1_phone}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Form.Group className='mb-3'>
							<Form.Label>Address</Form.Label>
							<Form.Control
								as='textarea'
								rows={2}
								name='parent1_address'
								value={formData.parent1_address}
								onChange={handleChange}
								disabled={student.locked}
							/>
						</Form.Group>
					</Tab>

					{/* Parent 2 Tab */}
					<Tab eventKey='parent2' title='Parent/Guardian 2'>
						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>First Name</Form.Label>
									<Form.Control
										type='text'
										name='parent2_firstName'
										value={formData.parent2_firstName}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Last Name</Form.Label>
									<Form.Control
										type='text'
										name='parent2_lastName'
										value={formData.parent2_lastName}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>

						<Row>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Email</Form.Label>
									<Form.Control
										type='email'
										name='parent2_email'
										value={formData.parent2_email}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
							<Col md={6}>
								<Form.Group className='mb-3'>
									<Form.Label>Phone</Form.Label>
									<Form.Control
										type='tel'
										name='parent2_phone'
										value={formData.parent2_phone}
										onChange={handleChange}
										disabled={student.locked}
									/>
								</Form.Group>
							</Col>
						</Row>
					</Tab>

					{/* Additional Info Tab */}
					<Tab eventKey='additional' title='Additional'>
						<Form.Group className='mb-3'>
							<Form.Label>Referrer</Form.Label>
							<Form.Control
								type='text'
								name='referrer'
								value={formData.referrer}
								onChange={handleChange}
								disabled={student.locked}
								placeholder='How did they hear about us?'
							/>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Form.Label>Notes</Form.Label>
							<Form.Control
								as='textarea'
								rows={5}
								name='notes'
								value={formData.notes}
								onChange={handleChange}
								disabled={student.locked}
								placeholder='Any additional notes about the student...'
							/>
						</Form.Group>
					</Tab>
				</Tabs>
			</Modal.Body>
			<Modal.Footer>
				<Button variant='secondary' onClick={onHide} disabled={saving}>
					Cancel
				</Button>
				<Button
					variant='primary'
					onClick={handleSave}
					disabled={saving || student.locked}
				>
					{saving ? (
						<>
							<i className='fa-sharp fa-solid fa-spinner fa-spin me-2'></i>
							Saving...
						</>
					) : (
						<>
							<i className='fa-sharp fa-solid fa-save me-2'></i>
							Save Changes
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default EditStudent;
